import { useMemo, useState } from 'react';
import {
  Card,
  Avatar,
  Typography,
  Box,
  Chip,
  useMediaQuery,
} from '@mui/material';
import DefaultCompany from './../../icons/building_round.svg';
import { useAccount } from '../../contexts/Account';
import eventBus, { eventBusValues } from '../../eventBus';
import AvatarsInvolved from '../../components/AvatarsInvolved';
import ACSnackbar from '../../components/ACSnackbar';
import { Dvr, RecordVoiceOver } from '@mui/icons-material';
import NativeShare from '../../components/Company/ShareItems/NativeShare';
import LinkedInLogo from '../../components/Company/ShareItems/linked_In_logo.svg';
import {
  isLinkedinAuthenticated,
  authorizeWithPopUp,
} from '../../services/LinkedIn';

function CompanyContact({ contact }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        mr: '20px',
        mb: '10px',
      }}
    >
      <Box sx={{ width: '51px', height: '51px' }}>
        <Avatar
          src={`https://${contact.image.location}` || DefaultCompany}
          sx={{ width: '51px', height: '51px' }}
        />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', mx: '10px' }}>
        <Typography
          sx={{
            fontSize: '16px',
            fontWeight: 700,
            color: '#001030',
            lineHeight: '24px',
            whiteSpace: 'nowrap',
          }}
        >
          {contact.displayName}
        </Typography>
        <Typography
          sx={{
            fontSize: '16px',
            fontWeight: 500,
            color: '#001030',
            lineHeight: '22px',
          }}
        >
          {contact.title}
        </Typography>
      </Box>
    </Box>
  );
}

export default function BoardroomHeader() {
  const { accountDetails } = useAccount();
  const [snackbar, setSnackbar] = useState(false);
  const boardOwner = accountDetails?.contacts?.find(
    (contact) => contact.isBoardOwner === true
  );
  const isSM = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const isXS = useMediaQuery((theme) => theme.breakpoints.down('xs'));

  const activeSortedBoardAdvisors = useMemo(() => {
    if (!accountDetails.boardAdvisors) return [];
    return accountDetails.boardAdvisors
      ?.filter(
        (advisor) =>
          advisor.advisorStatus === 'active' || advisor.advisorStatus === 'new'
      )
      .sort((a, b) => b.joinedOnDate.localeCompare(a.joinedOnDate))
      .map((bm) => {
        const advisor = bm.advisor || {};
        if (advisor.image && advisor.image.imageKey) {
          return `${process.env.REACT_APP_IMAGES_URL}${advisor.image.imageKey}`;
        }
        return null;
      });
  }, []);

  function openProfile() {
    eventBus.dispatch(eventBusValues.triggerCompanyDrawerAction, {
      company: accountDetails,
    });
  }

  const handleCopyLink = () => {
    navigator.clipboard.writeText(
      `https://app.advisorycloud.com/join/advisors/${accountDetails.slug}/${accountDetails.id}`
    );
    setSnackbar(true);
  };

  const iconStyle = {
    margin: '0 10px',
    cursor: 'pointer',
  };

  return (
    <Card
      sx={{
        p: 3,
        borderRadius: '8px',
        boxShadow: 'none',
        height: !isSM ? '100%' : 'auto',
        display: 'flex',
        flexDirection: { xxs: 'column', sm: 'row' },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: { xxs: '100%', sm: 180 },
          height: { xxs: 128, xs: 140 },
          borderRadius: '8px',
        }}
      >
        <Avatar
          alt={`${accountDetails.companyName} Logo`}
          src={
            accountDetails &&
            accountDetails.image &&
            accountDetails.image.id &&
            !accountDetails.image.id.includes('undefined')
              ? `${process.env.REACT_APP_IMAGES_URL}${accountDetails.image.id}`
              : DefaultCompany
          }
          sx={{
            width: '140px',
            height: '140px',
          }}
          onClick={() => openProfile()}
        />
      </Box>
      <Box>
        <Typography
          id="boardroom-company-name"
          sx={{
            fontWeight: '600',
            fontSize: { xxs: 24, xs: 32 },
            textAlign: { xxs: 'center', sm: 'left' },
            lineHeight: '41px',
            color: '#001030',
          }}
        >
          {accountDetails.companyName} Advisory Board
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xxs: 'column', sm: 'row' },
            justifyContent: 'flex-start',
            alignItems: { xxs: 'center', sm: 'flex-start' },
            mt: 2,
            gap: 2,
            maxWidth: '100%',
          }}
        >
          {boardOwner && <CompanyContact contact={boardOwner} />}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              gap: 2,
            }}
          >
            <AvatarsInvolved
              noText
              limit={5}
              avatars={activeSortedBoardAdvisors}
              avatarWidth={'41px'}
            />
            {activeSortedBoardAdvisors &&
              activeSortedBoardAdvisors.length > 5 && (
                <Chip
                  label={`+${activeSortedBoardAdvisors.length - 5} ${
                    activeSortedBoardAdvisors.length > 6
                      ? isXS
                        ? 'more'
                        : 'more advisors'
                      : 'more advisor'
                  }`}
                  sx={{
                    backgroundColor: '#E8EAED',
                    color: '#001030',
                    fontWeight: '500',
                    fontSize: 14,
                    lineHeight: '18px',
                    height: 32,
                    alignSelf: 'center',
                    mt: isXS ? '10px' : '0px',
                  }}
                />
              )}
          </Box>
        </Box>
        <Box
          display="flex"
          flexDirection={{ xxs: 'column', sm: 'row' }}
          alignItems="center"
          mt={2}
        >
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 700,
              color: '#4D586F',
              mr: '8px',
            }}
          >
            Advisory Board URL:
          </Typography>
          <Box
            onClick={handleCopyLink}
            id="own-company-profile-link"
            sx={{
              maxWidth: { xxs: '305px', xs: '405px' },
              border: '1px solid #EFF0F6',
              backgroundColor: '#FFFFFF',
              padding: '6px',
              borderRadius: '10px',
              cursor: 'pointer',
              flexGrow: 1,
              height: '35px',
            }}
          >
            <Typography
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: '12px',
                fontWeight: 400,
                letterSpacing: '-0.43px',
                paddingTop: '2px',
              }}
            >
              https://app.advisorycloud.com/join/advisors/
              {accountDetails.slug}/{accountDetails.id}
            </Typography>
          </Box>
        </Box>
        <Box
          display="flex"
          flexDirection={{ xxs: 'column', xs: 'row' }}
          alignItems="center"
          justifyContent={{ xxs: 'center', sm: 'flex-start' }}
          mt={2}
        >
          <Box display="flex" alignItems="center" mb={{ xxs: 2, xs: 0 }}>
            <RecordVoiceOver
              sx={{
                color: '#4FB5F9',
                mr: '8px',
              }}
            />
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: '16px',
                lineHeight: '24px',
                mr: '7px',
              }}
            >
              Invite Contacts
            </Typography>
          </Box>
          <Box>
            <img
              src={LinkedInLogo}
              alt="Linked In Logo"
              style={iconStyle}
              onClick={async () => {
                const isAuthenticatedLinkedIn = isLinkedinAuthenticated();
                if (isAuthenticatedLinkedIn) {
                  eventBus.dispatch('openLinkedInPostModal');
                } else {
                  await authorizeWithPopUp();
                }
              }}
            />
            <img
              src="/icons/facebook_logo.svg"
              alt="Facebook Logo"
              style={iconStyle}
              onClick={() => {
                eventBus.dispatch('openCopyModal', {
                  modalType: 'facebook',
                });
              }}
            />
            <img
              src="/icons/x_logo.svg"
              alt="X Logo"
              style={iconStyle}
              onClick={() => {
                eventBus.dispatch('openCopyModal', {
                  modalType: 'x',
                });
              }}
            />
            <NativeShare iconStyle={iconStyle} />
            <Dvr
              sx={{
                color: '#0866FF',
                m: '0px 10px',
                fontSize: '1.7rem',
                cursor: 'pointer',
              }}
              onClick={() => {
                eventBus.dispatch('openCopyModal');
              }}
            />
            <img
              src="/icons/email_icon.svg"
              alt="Email Logo"
              style={iconStyle}
              onClick={() => {
                eventBus.dispatch('openEmailModal');
              }}
            />
          </Box>
        </Box>
      </Box>
      <ACSnackbar
        open={snackbar}
        text="Link copied, you can now share it with whoever you choose"
        severity="success"
        onClose={() => setSnackbar(false)}
        autoHideDuration={6000}
      />
    </Card>
  );
}
